import { Slot } from "@radix-ui/react-slot";
import { cn } from "@stylingcv/utils";
import type { VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

import { buttonVariants } from "../variants/button";

export type ButtonProps = {
  asChild?: boolean;
  touchOptimized?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, touchOptimized = false, ...props }, ref) => {
    const Component = asChild ? Slot : "button";

    // Auto-apply touch-friendly sizes when touchOptimized is enabled
    let optimizedSize = size;
    if (touchOptimized && !size) {
      optimizedSize = "touch";
    } else if (touchOptimized && size) {
      // Map existing sizes to touch variants when possible
      const touchSizeMap = {
        sm: "touch-sm" as const,
        md: "touch" as const,
        lg: "touch-lg" as const,
        icon: "icon-lg" as const,
        default: "touch" as const,
      };
      optimizedSize = (touchSizeMap as any)[size] || size;
    }

    return (
      <Component
        ref={ref}
        className={cn(
          buttonVariants({ variant, size: optimizedSize, className }),
          // Add touch-manipulation for better mobile experience
          touchOptimized && "touch-manipulation",
        )}
        {...props}
      />
    );
  },
);

Button.displayName = "Button";

export { buttonVariants } from "../variants/button";
